import _ from 'lodash';
import { useCallback, useMemo } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import { ROLES } from '../../../_configuration';
import { getCurrMember } from './member.service';
import { getAssessments } from './assessment.service';
import { COMMUNICATION_LIST_QUERIES } from './email-log.service';
import { useLogQueryError } from '../hooks/use-log-query-error';
import {
  CommunicationContextType,
  DeliveryOption,
  IAdHocAudience,
  IAdHocCommunicationHistoryItem,
  IAdHocCommunicationInput,
  IAdHocCommunicationPreview,
  IAdHocContentInput,
  IAdHocRecipientSummary,
  IAdHocSendEvent,
  ICommunicationVariable,
  IComposeContextAgeGroup,
  IComposeContextSession,
  ICountAdHocRecipientsResult
} from '../types/communication.types';
import {
  COUNT_AD_HOC_RECIPIENTS,
  GET_AD_HOC_COMMUNICATION_HISTORY,
  GET_AD_HOC_CONTEXT_VALUES,
  GET_AD_HOC_RECIPIENTS,
  GET_AD_HOC_SEND_EVENT_ITEMS,
  GET_AVAILABLE_VARIABLES,
  GET_COMPOSE_CONTEXT_AGE_GROUPS,
  GET_COMPOSE_CONTEXT_SESSIONS,
  GET_PREVIEW_AD_HOC_COMMUNICATION,
  RESEND_AD_HOC_SEND_EVENT_FAILED,
  SEND_AD_HOC_COMMUNICATION
} from '../queries/communication.queries';

// Permission gate — a hook despite the name (calls getCurrMember/getAssessments),
// so call at top level. See CLAUDE.md "Extract a feature-specific permission helper".
export const hasCommunicationAccess = (): { hasCommunication: boolean; loading: boolean } => {
  const { member, loading: memberLoading } = getCurrMember();
  const { assessments, loading: assessmentsLoading } = getAssessments({ requiredRoles: [ROLES.COMMUNICATION] });

  // useMemo keeps the returned object referentially stable for consumer deps
  // (CLAUDE.md "Extract a feature-specific permission helper").

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMemo(() => ({
    hasCommunication: !!member && assessments.length > 0,
    loading: memberLoading || assessmentsLoading
  }), [member, assessments, memberLoading, assessmentsLoading]);
};

// Refetch by operation *name* (not document) so every active observable re-runs
// with its own variables (the staff HistoryPanel and any open player page).
export const AD_HOC_HISTORY_REFETCH_OPERATIONS = [
  'GetAdHocCommunicationHistory',
  'GetAdHocSendEventItems',
  ...COMMUNICATION_LIST_QUERIES,
];

export const buildSendAdHocCommunication = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(SEND_AD_HOC_COMMUNICATION, {
    // The mutation returns counts, not the new Communication rows, so Apollo
    // can't auto-merge them — refetch the history surfaces to update both views.
    refetchQueries: AD_HOC_HISTORY_REFETCH_OPERATIONS,
    awaitRefetchQueries: false
  });

  // useCallback keeps the wrapper reference-stable so consumers can pass it as a
  // hook dep or to memoized children without churning on every render.

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sendAdHocCommunication = useCallback(
    (input: IAdHocCommunicationInput) => mutation({ variables: { input } }),
    [mutation]
  );

  return { ...rest, sendAdHocCommunication };
};

export const useCountAdHocRecipients = (
  { assessmentId, criteria, deliveryOption, contextType, contextId }:
  {
    assessmentId?: string;
    criteria: IAdHocAudience;
    deliveryOption: DeliveryOption;
    contextType?: CommunicationContextType;
    contextId?: string;
  }
): ICountAdHocRecipientsResult => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useQuery(COUNT_AD_HOC_RECIPIENTS, {
    skip: !assessmentId,
    variables: { assessmentId, criteria, deliveryOption, contextType, contextId },
    fetchPolicy: 'cache-and-network'
  });

  useLogQueryError(error, 'countAdHocRecipients');
  return {
    recipients: _.get(data, 'countAdHocRecipients.recipients', 0),
    messages: _.get(data, 'countAdHocRecipients.messages', 0),
    unreachable: _.get(data, 'countAdHocRecipients.unreachable', 0),
    loading,
    error
  };
};

export const getAdHocCommunicationHistory = (
  { assessmentId, search, includeTests = false, offset = 0, limit = 25 }:
  { assessmentId?: string; search?: string; includeTests?: boolean; offset?: number; limit?: number }
): {
  events: IAdHocSendEvent[];
  total: number;
  loading: boolean;
  error?: ApolloError;
  // Imperative poll controls (from Apollo) so the panel can poll only while a
  // visible event is still draining the delivery queue.
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
} => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error, startPolling, stopPolling } = useQuery(GET_AD_HOC_COMMUNICATION_HISTORY, {
    skip: !assessmentId,
    variables: { assessmentId, search: search || undefined, includeTests, offset, limit },
    fetchPolicy: 'cache-and-network'
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLogQueryError(error, 'adHocCommunicationHistory');
  return {
    events: _.get(data, 'adHocCommunicationHistory.items', []) as IAdHocSendEvent[],
    total: _.get(data, 'adHocCommunicationHistory.total', 0),
    loading,
    error,
    startPolling,
    stopPolling
  };
};

// Paged + searchable recipient rows for one send, loaded on expand (skip while
// the row is collapsed so the query doesn't run for every event in the list).
export const getAdHocSendEventItems = (
  { assessmentId, communicationGroupId, search, offset = 0, limit = 25, skip = false }:
  { assessmentId?: string; communicationGroupId: string; search?: string; offset?: number; limit?: number; skip?: boolean }
): { items: IAdHocCommunicationHistoryItem[]; total: number; loading: boolean; error?: ApolloError } => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useQuery(GET_AD_HOC_SEND_EVENT_ITEMS, {
    skip: skip || !assessmentId || !communicationGroupId,
    variables: { assessmentId, communicationGroupId, search: search || undefined, offset, limit },
    fetchPolicy: 'cache-and-network'
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLogQueryError(error, 'adHocSendEventItems');
  return {
    items: _.get(data, 'adHocSendEventItems.items', []) as IAdHocCommunicationHistoryItem[],
    total: _.get(data, 'adHocSendEventItems.total', 0),
    loading,
    error
  };
};

export const buildResendAdHocSendEventFailed = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(RESEND_AD_HOC_SEND_EVENT_FAILED, {
    // A bulk retry changes per-row statuses — refetch by operation name so every
    // active observable (HistoryPanel + any open player page) updates.
    refetchQueries: AD_HOC_HISTORY_REFETCH_OPERATIONS,
    awaitRefetchQueries: false
  });

  // useCallback keeps the wrapper reference-stable (see buildSendAdHocCommunication).

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const resendAdHocSendEventFailed = useCallback(
    (communicationGroupId: string) => mutation({ variables: { communicationGroupId } }),
    [mutation]
  );

  return { ...rest, resendAdHocSendEventFailed };
};

// cache-first: the registry is static per deploy, so one fetch per session.
export const useAvailableVariables = (
  assessmentId?: string
): { variables: ICommunicationVariable[]; loading: boolean; error?: ApolloError } => {
  const { data, loading, error } = useQuery(GET_AVAILABLE_VARIABLES, {
    skip: !assessmentId,
    variables: { assessmentId },
    fetchPolicy: 'cache-first'
  });

  // Memoized off the Apollo-stable data object so consumers can feed the array
  // to their own memo/effect deps without churn.
  const variables = useMemo(
    () => _.get(data, 'availableVariables', []) as ICommunicationVariable[],
    [data]
  );

  useLogQueryError(error, 'availableVariables');
  return { variables, loading, error };
};

// Paged + searchable audience rows; skip while the recipient list is closed.
export const useAdHocRecipients = (
  { assessmentId, criteria, contextType, contextId, search, offset = 0, limit = 50, skip = false }:
  {
    assessmentId?: string;
    criteria: IAdHocAudience;
    contextType?: CommunicationContextType;
    contextId?: string;
    search?: string;
    offset?: number;
    limit?: number;
    skip?: boolean;
  }
): { items: IAdHocRecipientSummary[]; total: number; loading: boolean; error?: ApolloError } => {
  const { data, loading, error } = useQuery(GET_AD_HOC_RECIPIENTS, {
    skip: skip || !assessmentId,
    variables: { assessmentId, criteria, contextType, contextId, search: search || undefined, offset, limit },
    fetchPolicy: 'cache-and-network'
  });

  const items = useMemo(
    () => _.get(data, 'adHocRecipients.items', []) as IAdHocRecipientSummary[],
    [data]
  );

  useLogQueryError(error, 'adHocRecipients');
  return { items, total: _.get(data, 'adHocRecipients.total', 0), loading, error };
};

// One shared call powers token validation + preview; the caller owns the debounce,
// so it passes already-debounced content and gates with skip while idle.
export const usePreviewAdHocCommunication = (
  { assessmentId, criteria, contextType, contextId, content, playerAssessmentId, skip = false }:
  {
    assessmentId?: string;
    criteria: IAdHocAudience;
    contextType?: CommunicationContextType;
    contextId?: string;
    content: IAdHocContentInput;
    playerAssessmentId?: string;
    skip?: boolean;
  }
): { preview?: IAdHocCommunicationPreview; loading: boolean; error?: ApolloError } => {
  // no-cache: every content edit is a unique variables tuple, so cached entries
  // would never be re-read but would bloat the persisted Apollo cache.
  const { data, loading, error } = useQuery(GET_PREVIEW_AD_HOC_COMMUNICATION, {
    skip: skip || !assessmentId,
    variables: { assessmentId, criteria, contextType, contextId, content, playerAssessmentId },
    fetchPolicy: 'no-cache'
  });

  useLogQueryError(error, 'previewAdHocCommunication');
  return {
    preview: _.get(data, 'previewAdHocCommunication') as IAdHocCommunicationPreview | undefined,
    loading,
    error
  };
};

// Distinct level/team values for the audience pickers.
export const useAdHocContextValues = (
  { assessmentId, skip = false }: { assessmentId?: string; skip?: boolean }
): { levels: string[]; teams: string[]; loading: boolean; error?: ApolloError } => {
  const { data, loading, error } = useQuery(GET_AD_HOC_CONTEXT_VALUES, {
    skip: skip || !assessmentId,
    variables: { assessmentId },
    fetchPolicy: 'cache-and-network'
  });

  const levels = useMemo(() => _.get(data, 'adHocContextValues.levels', []) as string[], [data]);
  const teams = useMemo(() => _.get(data, 'adHocContextValues.teams', []) as string[], [data]);

  useLogQueryError(error, 'adHocContextValues');
  return { levels, teams, loading, error };
};

// Age groups + open sessions for the compose context pickers, keyed by the
// panel's assessment (never the active-assessment global the age-group service uses).
export const useComposeContextOptions = (
  assessmentId?: string,
  skip = false
): {
  ageGroups: IComposeContextAgeGroup[];
  sessions: IComposeContextSession[];
  loading: boolean;
  error?: ApolloError;
} => {
  const { data: ageGroupData, loading: ageGroupsLoading, error: ageGroupsError } = useQuery(
    GET_COMPOSE_CONTEXT_AGE_GROUPS,
    { skip: skip || !assessmentId, variables: { assessmentId }, fetchPolicy: 'cache-and-network' }
  );

  // Memoized off the Apollo-stable data object so the ids array (the session
  // query's variables) stays referentially stable across renders.
  const ageGroups = useMemo(
    () => _.get(ageGroupData, 'ageGroups', []) as IComposeContextAgeGroup[],
    [ageGroupData]
  );
  const ageGroupIds = useMemo(() => ageGroups.map((group) => group._id), [ageGroups]);

  // assessmentSessions requires a non-empty ageGroupIds (empty returns []), so
  // the session query chains behind the age-group result.
  const { data: sessionData, loading: sessionsLoading, error: sessionsError } = useQuery(
    GET_COMPOSE_CONTEXT_SESSIONS,
    {
      skip: skip || !assessmentId || !ageGroupIds.length,
      variables: { assessmentId, ageGroupIds },
      fetchPolicy: 'cache-and-network'
    }
  );

  const sessions = useMemo(
    () => _.get(sessionData, 'assessmentSessions', []) as IComposeContextSession[],
    [sessionData]
  );

  const error = ageGroupsError || sessionsError;
  useLogQueryError(error, 'composeContextOptions');
  return {
    ageGroups,
    sessions,
    loading: ageGroupsLoading || sessionsLoading,
    error
  };
};
